import React, { useEffect, useState } from 'react';

import { timingBridgeAPI, toolsAPI } from '../../../lib/api';
import { formatDuration } from '../climbBridge/PlaybackTransport';

const TOOL_KEY = 'timing-bridge';
const FIELD = 'w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500';
const LABEL = 'block text-[10px] font-medium text-gray-500 mb-1 uppercase tracking-wide';
const BUTTON = 'px-3 py-1.5 text-xs rounded border border-[#2a2a2a] bg-[#0a0a0a] text-gray-300 hover:border-[#3a3a3a] disabled:opacity-40';
const SPEEDS = [0.5, 1, 2, 4, 8, 16];

const isSeries = (p) => p?.config?.kind === 'series' || Object.values(p?.config?.streams || {}).some(Boolean);

const formatBytes = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`);

/**
 * Screen 4 — Recordings.
 *
 * Live timing is recorded as it runs, so a race can be played back into
 * Singular between events — with the real streams, the live championships and
 * the broadcast page all behaving as they would on the day. And when there is
 * no race to record, a test meeting can be generated in Speedhive's exact
 * format from a real championship's riders.
 */
export default function RecordingsPanel({ show, summary, onChanged, onPlaybackStarted }) {
  const [recordings, setRecordings] = useState([]);
  const [presets, setPresets] = useState([]);
  const [seriesId, setSeriesId] = useState('');
  const [speed, setSpeed] = useState(1);
  const [loop, setLoop] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [renaming, setRenaming] = useState(null);
  const [sim, setSim] = useState({ championship: '', class_name: 'MX1', groups: '', motos: 2, race_minutes: 15, lap_seconds: 125, field_size: 30, seed: 8 });

  const running = !!summary?.running;
  const live = running && summary?.mode !== 'playback';
  const recording = summary?.recording;
  const seriesList = presets.filter(isSeries);
  const series = seriesList.find(p => p.id === seriesId) || null;
  const championshipsInSeries = series?.config?.championships || [];

  function load() {
    return timingBridgeAPI.recordings().then(res => setRecordings(res.data.recordings || [])).catch(() => {});
  }

  useEffect(() => {
    load();
    toolsAPI.presets(TOOL_KEY).then((res) => {
      const list = (res.data || []).filter(isSeries);
      setPresets(res.data || []);
      if (list.length === 1) setSeriesId(list[0].id);
    }).catch(() => {});
  }, []);

  // Default the test race to the series' MX1 championship, which is what it was asked for.
  useEffect(() => {
    if (sim.championship || !championshipsInSeries.length) return;
    const mx1 = championshipsInSeries.find(c => /mx1/i.test(c.label)) || championshipsInSeries[0];
    setSim(s => ({ ...s, championship: mx1.url, class_name: mx1.match || mx1.label, groups: mx1.system === 'bssc' ? (Array.isArray(mx1.groups) ? mx1.groups.join(', ') : (mx1.groups || '')) : '', motos: mx1.system === 'bssc' ? 3 : 2 }));
  }, [championshipsInSeries]); // eslint-disable-line react-hooks/exhaustive-deps

  async function act(name, fn, done) {
    setBusy(name);
    setError('');
    setNotice('');
    try {
      const res = await fn();
      done?.(res);
      await load();
      onChanged?.();
    } catch (err) {
      const data = err.response?.data;
      const details = (data?.details || []).map(d => `${d.field || 'request'}: ${d.message}`).join('; ');
      setError([data?.error || err.message || 'That did not work', details].filter(Boolean).join(': '));
    } finally {
      setBusy('');
    }
  }

  function play(rec) {
    if (!show) { setError('Pick a show first: playback runs the bridge for a show.'); return; }
    act(`play-${rec.id}`, () => timingBridgeAPI.startPlayback({
      show_id: show.id,
      recording_id: rec.id,
      streams_preset_id: series?.id || null,
      speed,
      loop,
    }), () => onPlaybackStarted?.());
  }

  function generate() {
    act('simulate', () => timingBridgeAPI.simulate({
      championship_url: sim.championship.trim(),
      class_name: sim.class_name.trim() || 'MX1',
      motos: Number(sim.motos) || 2,
      groups: sim.groups.split(',').map(g => g.trim()).filter(Boolean),
      race_minutes: Number(sim.race_minutes) || 15,
      lap_seconds: Number(sim.lap_seconds) || 125,
      field_size: Number(sim.field_size) || 30,
      seed: Number(sim.seed) || 8,
    }), (res) => {
      const r = res.data.results || [];
      setNotice(`Made "${res.data.name}" (${formatDuration(res.data.duration_ms)}). ${r.map(x => `${x.label}: ${x.top.join(', ')}`).join(' · ')}`);
    });
  }

  return (
    <div className="space-y-6">
      {error && <div className="px-4 py-3 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">{error}</div>}
      {notice && <div className="px-4 py-3 bg-green-900/20 border border-green-800 rounded text-green-300 text-xs leading-relaxed">{notice}</div>}

      {/* Recording the live bridge */}
      {live && (
        <section className="px-4 py-3 bg-[#111111] border border-[#2a2a2a] rounded flex flex-wrap items-center gap-3">
          {recording
            ? (
              <>
                <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                <span className="text-sm text-white">Recording {recording.name ? `"${recording.name}"` : ''}</span>
                <span className="text-xs text-gray-500">{formatDuration(recording.durationMs)} · {recording.entries} responses</span>
                <button onClick={() => act('stop-rec', () => timingBridgeAPI.stopRecording(show.id))} disabled={!!busy} className={`${BUTTON} ml-auto`}>
                  {busy === 'stop-rec' ? 'Saving…' : 'Stop recording'}
                </button>
              </>
            )
            : (
              <>
                <span className="text-sm text-gray-400">Live timing is running and not recording.</span>
                <button onClick={() => act('start-rec', () => timingBridgeAPI.startRecording(show.id))} disabled={!!busy} className={`${BUTTON} ml-auto`}>Record now</button>
              </>
            )}
        </section>
      )}

      {/* Where playback goes */}
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-white">Play into Singular</h2>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
          <div>
            <label className={LABEL}>Race series (its streams and championships)</label>
            <select value={seriesId} onChange={e => setSeriesId(e.target.value)} className={FIELD}>
              <option value="">The bridge running now</option>
              {seriesList.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {seriesList.length === 0 && (
              <p className="text-[10px] text-amber-500 mt-1">No race series in this workspace yet. Make one on the Setup tab with its championships (and its data streams to send graphics) so playback has standings to work out.</p>
            )}
          </div>
          <div>
            <label className={LABEL}>Speed</label>
            <div className="flex gap-1">
              {SPEEDS.map(s => (
                <button key={s} onClick={() => setSpeed(s)} className={`px-2 py-2 text-xs rounded border ${speed === s ? 'bg-blue-600 border-blue-500 text-white' : 'bg-[#0a0a0a] border-[#2a2a2a] text-gray-400'}`}>{s}×</button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-400 pb-2">
            <input type="checkbox" checked={loop} onChange={e => setLoop(e.target.checked)} /> Loop
          </label>
        </div>
        <p className="text-[10px] text-gray-600 leading-relaxed">
          Playback publishes to the real data streams of the series, and its championships and the broadcast page run as they would live,
          so graphics can be built with data actually flowing. It stops whatever is running for {show?.name || 'the show'} first. Speed,
          pause, scrubbing and loop are on the Live tab while it plays.
        </p>
      </section>

      {/* Generate a race */}
      <section className="px-4 py-3 bg-[#111111] border border-[#2a2a2a] rounded space-y-3">
        <h2 className="text-sm font-semibold text-white">Generate a test race</h2>
        <p className="text-[11px] text-gray-500 leading-relaxed">
          A made-up meeting in Speedhive's exact format, raced by a real championship's riders so the live standings react: in moto 1
          the championship leader crashes and second in the table wins; in moto 2 the leader fights back; in moto 3, third in the table wins. Each has a retirement, a
          mechanics'-area stop and a battle, and a break between them so moto 1 banks as it would on the day.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
          <div className="col-span-2 sm:col-span-6">
            <label className={LABEL}>Championship (whose riders race)</label>
            {championshipsInSeries.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-1.5">
                {championshipsInSeries.map(c => (
                  <button key={c.label} onClick={() => setSim(s => ({ ...s, championship: c.url, class_name: c.match || c.label, groups: c.system === 'bssc' ? (Array.isArray(c.groups) ? c.groups.join(', ') : (c.groups || '')) : '', motos: c.system === 'bssc' ? 3 : 2 }))}
                    className={`px-2.5 py-1 text-xs rounded border ${sim.championship === c.url ? 'bg-blue-600 border-blue-500 text-white' : 'bg-[#0a0a0a] border-[#2a2a2a] text-gray-400'}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            )}
            <input value={sim.championship} onChange={e => setSim(s => ({ ...s, championship: e.target.value }))} placeholder="Speedhive championship URL or standings Sheet" className={FIELD} />
          </div>
          <Num label="Class in title" value={sim.class_name} onChange={v => setSim(s => ({ ...s, class_name: v }))} text />
          <Num label="Races" value={sim.motos} min={1} max={3} onChange={v => setSim(s => ({ ...s, motos: v }))} />
          <Num label="Minutes + 2 laps" value={sim.race_minutes} min={3} max={40} onChange={v => setSim(s => ({ ...s, race_minutes: v }))} />
          <Num label="Lap (sec)" value={sim.lap_seconds} min={40} max={240} onChange={v => setSim(s => ({ ...s, lap_seconds: v }))} />
          <Num label="Riders" value={sim.field_size} min={6} max={80} onChange={v => setSim(s => ({ ...s, field_size: v }))} />
          <Num label="Seed" value={sim.seed} min={1} max={1000000} onChange={v => setSim(s => ({ ...s, seed: v }))} />
          <div className="col-span-2 sm:col-span-6">
            <label className={LABEL}>Groups (optional, best first)</label>
            <input value={sim.groups} onChange={e => setSim(s => ({ ...s, groups: e.target.value }))} placeholder="e.g. Group A, Group B: each race runs once per group, lowest first, 18 riders in A and 20 in B" className={FIELD} />
          </div>
        </div>
        <button onClick={generate} disabled={!!busy || !sim.championship.trim()} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white text-sm rounded">
          {busy === 'simulate' ? 'Generating…' : 'Generate'}
        </button>
      </section>

      {/* The library */}
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-white">Recordings</h2>
        {!recordings.length && <p className="text-xs text-gray-500">None yet. Live timing records from the start, or generate a test race above.</p>}
        <div className="space-y-1.5">
          {recordings.map(rec => (
            <div key={rec.id} className="flex flex-wrap items-center gap-3 px-3 py-2 bg-[#111111] border border-[#2a2a2a] rounded">
              <div className="min-w-0 flex-1">
                {renaming?.id === rec.id
                  ? (
                    <input
                      autoFocus
                      value={renaming.name}
                      onChange={e => setRenaming({ ...renaming, name: e.target.value })}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && renaming.name.trim()) act('rename', () => timingBridgeAPI.renameRecording(rec.id, renaming.name.trim()), () => setRenaming(null));
                        if (e.key === 'Escape') setRenaming(null);
                      }}
                      className={FIELD}
                    />
                  )
                  : <div className="text-sm text-white truncate">{rec.name}</div>}
                <div className="text-[11px] text-gray-500">
                  {rec.synthetic ? 'generated' : rec.provider} · {formatDuration(rec.duration_ms)} · {formatBytes(rec.bytes)} · {new Date(rec.started_at).toLocaleString()}
                  {rec.status === 'recording' && <span className="ml-2 text-red-400">recording</span>}
                  {rec.status === 'interrupted' && <span className="ml-2 text-amber-500">cut off</span>}
                  {rec.playing && <span className="ml-2 text-blue-400">playing</span>}
                </div>
              </div>
              <button onClick={() => play(rec)} disabled={!!busy || rec.status === 'recording'} className="px-3 py-1.5 text-xs rounded bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40">
                {busy === `play-${rec.id}` ? 'Starting…' : 'Play'}
              </button>
              <button onClick={() => setRenaming({ id: rec.id, name: rec.name })} className="text-[11px] text-gray-500 hover:text-gray-300">Rename</button>
              <button
                onClick={() => { if (window.confirm(`Delete "${rec.name}"? This cannot be undone.`)) act(`del-${rec.id}`, () => timingBridgeAPI.deleteRecording(rec.id)); }}
                disabled={!!busy}
                className="text-[11px] text-gray-500 hover:text-red-400"
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Num({ label, value, onChange, min, max, text = false }) {
  return (
    <div>
      <label className={LABEL}>{label}</label>
      <input type={text ? 'text' : 'number'} min={min} max={max} value={value} onChange={e => onChange(e.target.value)} className={FIELD} />
    </div>
  );
}
